import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { Box, Grid2 as Grid, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import OrderCommentInput from '../common/_inputs/OrderComment';
import InputFile from '../common/_inputs/InputFile';
import {
  OrderFlowModal,
  PickupFlowIllustration,
} from './components/OrderFlowModal';
import {
  CustomDeliveryMessage,
  LocationAlertTutorialLink,
  LocationButton,
  LocationInput,
} from './components';
import DynamicRecipientFields from './components/DynamicRecipientFields';
import SignatureModal from '../common/components/SignatureModal';
import { MAX_ATTEMPTS_LOCATION } from '../../config/consts/commons';
import {
  RECIPIENT_NAME_MAX_LENGTH,
  VALID_RECIPIENT_NAME_REGEX_PATTERN,
} from '../../config/consts/orders';
import { normalizeName } from '../../utils';
import useOrdersToDeliverPickupType11Modal from './hooks/useOrdersToDeliverPickupType11Modal';

const OrdersToDeliverPickupType11Modal = () => {
  const { t } = useTranslation(['description', 'button', 'validation']);
  const { ordersModalConfig } = useSelector((state) => state.orders);
  const { show, titleOpening, order } = ordersModalConfig;

  const {
    step,
    setStep,
    config,
    computeRulesStep1,
    computeRulesStep2,
    setLocation,
    customLocation,
    setCustomLocation,
    locationStatus,
    setLocationStatus,
    attempts,
    setAttempts,
    errorMessage,
    setErrorMessage,
    setComment,
    recipient,
    setRecipient,
    recipientDocument,
    setRecipientDocument,
    recipientRelationship,
    setRecipientRelationship,
    signatureModalOpen,
    setSignatureModalOpen,
    signatureData,
    isSubmitting,
    showSubmitError,
    submitDelivered,
    handleSignatureConfirm,
    resetModal,
    loadReceiptTemplate,
  } = useOrdersToDeliverPickupType11Modal();

  const [file, setFile] = useState(null);
  const [isFileCompressing, setIsFileCompressing] = useState(false);

  useEffect(() => {
    loadReceiptTemplate();
  }, [loadReceiptTemplate]);

  if (!order || !titleOpening) return null;

  const submitDisabled =
    step === 1
      ? computeRulesStep1({ file, isFileCompressing })
      : computeRulesStep2({ file, isFileCompressing });

  const onConfirm = () => {
    if (computeRulesStep1({ file, isFileCompressing })) return;
    void submitDelivered({ file });
  };

  const handleSecondary = () => {
    resetModal();
  };

  const locationBlock = (
    <>
      <LocationButton
        setLocation={setLocation}
        locationStatus={locationStatus}
        setLocationStatus={setLocationStatus}
        setErrorMessage={setErrorMessage}
        attempts={attempts}
        setAttempts={setAttempts}
      />

      {errorMessage && locationStatus === 'error' && (
        <LocationAlertTutorialLink />
      )}
      {locationStatus === 'error' && attempts >= MAX_ATTEMPTS_LOCATION && (
        <LocationInput
          customLocation={customLocation}
          setCustomLocation={setCustomLocation}
        />
      )}
    </>
  );

  const attachPhotoLabel = t(
    'orders.deliver.pickup_type11.attach_pickup_photo',
    {
      ns: 'description',
    }
  );

  return (
    <>
      <OrderFlowModal
        open={show}
        onClose={resetModal}
        titleOpening={titleOpening}
        orderCode={order.order_code}
        storeName={order.ecommerce_name ?? order.id_ecommerce}
        primaryLabel={t('confirm', { ns: 'button' })}
        secondaryLabel={t('back', { ns: 'button' })}
        onPrimary={onConfirm}
        onSecondary={handleSecondary}
        primaryDisabled={submitDisabled}
        isSubmitting={isSubmitting}
        showSubmitError={showSubmitError}
      >
        {step === 1 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              width: '100%',
              pointerEvents: isSubmitting ? 'none' : 'auto',
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            <PickupFlowIllustration />
            <InputFile
              label={attachPhotoLabel}
              onFileReady={setFile}
              onProcessingChange={setIsFileCompressing}
              inputId="file-pickup-type11-step1"
            />
            {locationBlock}
            <OrderCommentInput
              setComment={(v) => setComment(v)}
              showOptionalCaption
            />
          </Box>
        )}

        {false && step === 2 && (
          <Grid
            container
            spacing={3}
            sx={{
              pointerEvents: isSubmitting ? 'none' : 'auto',
              userSelect: isSubmitting ? 'none' : 'auto',
              opacity: isSubmitting ? 0.6 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            <Grid size={12}>
              <CustomDeliveryMessage idEcommerce={order.ecommerce_name} />
            </Grid>

            <Grid size={12}>
              <InputFile
                label={attachPhotoLabel}
                onFileReady={setFile}
                onProcessingChange={setIsFileCompressing}
                inputId="file-pickup-type11-step2"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                label={t('orders.deliver.addressee_name', {
                  ns: 'description',
                })}
                placeholder={t('orders.deliver.addressee_name_placeholder', {
                  ns: 'description',
                  defaultValue: 'Nome completo',
                })}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: RECIPIENT_NAME_MAX_LENGTH }}
                error={
                  recipient.length > RECIPIENT_NAME_MAX_LENGTH ||
                  (recipient.length > 0 &&
                    !VALID_RECIPIENT_NAME_REGEX_PATTERN.test(
                      normalizeName(recipient)
                    ))
                }
                helperText={
                  recipient.length > 0 &&
                  !VALID_RECIPIENT_NAME_REGEX_PATTERN.test(
                    normalizeName(recipient)
                  )
                    ? t('validation.invalid_name', {
                        ns: 'validation',
                        defaultValue: 'Formato de nombre inválido',
                      })
                    : ''
                }
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6)' },
                }}
              />
            </Grid>

            <DynamicRecipientFields
              config={config}
              values={{
                recipientDocument,
                recipientRelationship,
                signatureData,
              }}
              handlers={{
                onRecipientDocumentChange: (e) =>
                  setRecipientDocument(e.target.value),
                onRecipientRelationshipChange: (e) =>
                  setRecipientRelationship(e.target.value),
                onOpenSignatureModal: () => setSignatureModalOpen(true),
              }}
            />

            <Grid size={12}>{locationBlock}</Grid>

            <Grid size={12}>
              <OrderCommentInput setComment={(v) => setComment(v)} />
            </Grid>
          </Grid>
        )}
      </OrderFlowModal>

      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onConfirm={handleSignatureConfirm}
        initialSignature={signatureData}
      />
    </>
  );
};

export default OrdersToDeliverPickupType11Modal;
